/**
 * Client typé des edge functions du blog réflexif (admin).
 *
 * `blog-article-generate` (LLM Opus → brouillon + slug), `blog-article-update`
 * (patch des champs éditables), et la couverture via `generate-illustration`
 * (entity_type='blog'). Calque le pattern `invokeOrThrow` de exercise-v2.
 */

import { supabase } from '@/lib/supabase';
import type {
  BlogArticle,
  BlogArticleLength,
  BlogBlockKind,
  BlogPoll,
  BlogEvaluation,
  BlogAxisScores,
  BlogBlockEvaluation,
} from '@jeraisonne/db';

export class BlogError extends Error {
  constructor(
    message: string,
    public readonly code?: string,
  ) {
    super(message);
    this.name = 'BlogError';
  }
}

async function invokeBlog<T>(fn: string, body: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke<T>(fn, { body });
  if (error) {
    let message = error.message;
    let code: string | undefined;
    const ctx = (error as Error & { context?: Response }).context;
    if (ctx && typeof ctx.json === 'function') {
      try {
        const parsed = (await ctx.json()) as { error?: string; message?: string };
        code = parsed.error;
        if (parsed.message) message = parsed.message;
        else if (parsed.error) message = parsed.error;
      } catch {
        /* garde le message générique */
      }
    }
    throw new BlogError(message, code);
  }
  if (!data) throw new BlogError('empty_response');
  return data;
}

export interface GenerateBlogInput {
  subject: string;
  sources?: string;
  references?: string;
  options?: { tone?: string; audience?: string };
  /** Longueur visée du contenu (verbosité), structure inchangée. Défaut `normal`. */
  length?: BlogArticleLength;
  /** Override de modèle (sinon défaut = Opus). */
  model?: string;
}

/** La génération est ASYNCHRONE : renvoie l'id tout de suite (status='generating'). */
export interface GenerateBlogResult {
  ok: boolean;
  article_id: string;
  slug?: string;
  status: 'generating';
}

export function generateBlogArticle(input: GenerateBlogInput): Promise<GenerateBlogResult> {
  return invokeBlog('blog-article-generate', {
    subject: input.subject.trim(),
    ...(input.sources?.trim() ? { sources: input.sources.trim() } : {}),
    ...(input.references?.trim() ? { references: input.references.trim() } : {}),
    ...(input.options ? { options: input.options } : {}),
    ...(input.length ? { length: input.length } : {}),
    ...(input.model ? { model: input.model } : {}),
  });
}

/** Relance la génération d'un article échoué (réutilise ses entrées). */
export function regenerateBlogArticle(articleId: string): Promise<GenerateBlogResult> {
  return invokeBlog('blog-article-generate', { article_id: articleId });
}

/** Entrée de (re)génération ciblée du sondage d'UN bloc (contenu courant). */
export interface GenerateBlogPollInput {
  blockIndex: number;
  blockCount: number;
  title: string;
  subject?: string;
  block: {
    kind: BlogBlockKind;
    heading: string;
    niveau1: string;
    niveau2: string;
    niveau3: string;
  };
}

/**
 * (Re)génère uniquement le sondage d'un bloc à partir de son contenu courant.
 * Ne persiste rien : renvoie le sondage, le client le fusionne dans le brouillon.
 */
export function generateBlogPoll(
  articleId: string,
  input: GenerateBlogPollInput,
): Promise<{ ok: boolean; poll: BlogPoll }> {
  return invokeBlog('blog-poll-generate', {
    article_id: articleId,
    block_index: input.blockIndex,
    block_count: input.blockCount,
    title: input.title,
    ...(input.subject?.trim() ? { subject: input.subject.trim() } : {}),
    block: input.block,
  });
}

/** Champs éditables d'un article (côté admin). */
export type BlogPatch = Partial<
  Pick<
    BlogArticle,
    | 'title'
    | 'subtitle'
    | 'slug'
    | 'status'
    | 'visibility'
    | 'illustration_url'
    | 'illustration_prompt'
    | 'blocks'
    | 'penseurs'
    | 'pour_aller_plus_loin'
    | 'social'
    | 'seo'
    | 'conclusion_md'
    | 'sources_utilisees'
    | 'reading_time'
    | 'published_at'
    | 'target_roles'
  >
>;

export function updateBlogArticle(
  articleId: string,
  patch: BlogPatch,
): Promise<{ ok: boolean; article: BlogArticle }> {
  return invokeBlog('blog-article-update', { article_id: articleId, patch });
}

// ─── Modération (tuteur soumet → admin approuve) ─────────────────────────────

/**
 * Soumet le brouillon à la validation admin (`submit`) ou le retire (`withdraw`).
 * Transitions : private → pending_review → (retrait) private. Renvoie l'article.
 */
export function submitBlogForReview(
  articleId: string,
  action: 'submit' | 'withdraw',
): Promise<{ ok: boolean; article: BlogArticle }> {
  return invokeBlog('blog-submit-for-review', { article_id: articleId, action });
}

/**
 * Modération admin d'un article `pending_review` : `approve` (→ public+published,
 * apparaît sur le fil /blog) ou `reject` (→ private). Renvoie l'article à jour.
 */
export function moderateBlogArticle(
  articleId: string,
  action: 'approve' | 'reject',
  reason?: string,
): Promise<{ ok: boolean; article: BlogArticle }> {
  return invokeBlog('blog-moderate', {
    article_id: articleId,
    action,
    ...(reason?.trim() ? { reason: reason.trim() } : {}),
  });
}

// ─── Raffinage manuel itératif : évaluation « juge » + versions par bloc ─────

// Évaluation MORCELÉE (évite l'idle-timeout 150 s + affichage progressif) :
// le client appelle 'global', puis 'block' pour chaque bloc, puis 'persist'.
// L'appelant doit avoir SAUVEGARDÉ le brouillon avant (l'edge fn lit la base).

export interface BlogGlobalEval {
  scores: BlogAxisScores;
  critique: string;
  axes_progression: string[];
}

/** Passe globale : note l'article au niveau global. */
export function evaluateBlogGlobal(
  articleId: string,
  threshold: number,
): Promise<{ ok: boolean; global: BlogGlobalEval }> {
  return invokeBlog('blog-article-evaluate', {
    phase: 'global',
    article_id: articleId,
    threshold,
  });
}

/** Évaluation d'UN bloc (+ son sondage). */
export function evaluateBlogBlock(
  articleId: string,
  threshold: number,
  blockIndex: number,
): Promise<{ ok: boolean; block: BlogBlockEvaluation }> {
  return invokeBlog('blog-article-evaluate', {
    phase: 'block',
    article_id: articleId,
    threshold,
    block_index: blockIndex,
  });
}

/** Persiste l'évaluation assemblée (1 ligne d'itération) + stampe les versions. */
export function persistBlogEvaluation(
  articleId: string,
  threshold: number,
  global: BlogGlobalEval,
  blocks: BlogBlockEvaluation[],
): Promise<{ ok: boolean; evaluation: BlogEvaluation }> {
  return invokeBlog('blog-article-evaluate', {
    phase: 'persist',
    article_id: articleId,
    threshold,
    evaluation: { global, blocks },
  });
}

/** Entrée du raffinage d'UN bloc (remarques manuelles + seuil). */
export interface RefineBlogBlockInput {
  blockIndex: number;
  manualRemarks?: string;
  threshold: number;
}

/**
 * Régénère les 3 niveaux d'un bloc en tenant compte de la critique du juge ET
 * de remarques manuelles. Conserve une version, écrit la version finale dans
 * `blocks[index]`, et renvoie l'article complet (re-hydratation du brouillon).
 */
export function refineBlogBlock(
  articleId: string,
  input: RefineBlogBlockInput,
): Promise<{ ok: boolean; article: BlogArticle }> {
  return invokeBlog('blog-article-refine-block', {
    article_id: articleId,
    block_index: input.blockIndex,
    ...(input.manualRemarks?.trim() ? { manual_remarks: input.manualRemarks.trim() } : {}),
    threshold: input.threshold,
  });
}

/** Bascule la version active d'un bloc (RPC atomique). Renvoie l'article à jour. */
export async function switchBlogBlockVersion(versionId: string): Promise<BlogArticle> {
  const { data, error } = await supabase.rpc('switch_blog_block_version', {
    p_version_id: versionId,
  });
  if (error) throw new BlogError(error.message, error.code);
  return data as unknown as BlogArticle;
}

/** Supprime une version de bloc (refuse l'active / la dernière, RPC). */
export async function deleteBlogBlockVersion(versionId: string): Promise<void> {
  const { error } = await supabase.rpc('delete_blog_block_version', { p_version_id: versionId });
  if (error) throw new BlogError(error.message, error.code);
}

/** Génère la couverture (16:9, modèle défini côté edge fn) et la stocke sur l'article. */
export async function generateBlogCover(
  articleId: string,
  prompt: string,
  title?: string,
): Promise<{ url: string | null }> {
  const res = await invokeBlog<{ url?: string; illustration_url?: string }>(
    'generate-illustration',
    {
      prompt,
      entity_type: 'blog',
      entity_id: articleId,
      ...(title ? { title } : {}),
    },
  );
  return { url: res.url ?? res.illustration_url ?? null };
}
